import { useEffect, useRef, useState } from 'react';
import {
  DownloadRequest,
  useStartDownloadTaskMutation,
  useValidateDownloadMutation,
  ValidateResult,
} from 'api';

/**
 * Button/flow state for the two-step validate-then-submit interaction:
 *
 *   idle ──validate()──▶ validating ──▶ validated ──submit()──▶ creating ──▶ done
 *                                  └─(over limit / invalid)─┘
 *
 * Any failed call moves to `error`. Changing the filter payload after a
 * successful validation snaps the flow back to `idle` so a stale row-count
 * estimate can never be submitted.
 */
export type ValidationPhase =
  | 'idle'
  | 'validating'
  | 'validated'
  | 'creating'
  | 'done'
  | 'error';

const errorMessage = (err: any, fallback: string): string => {
  const data = err?.data;
  if (typeof data === 'string' && data) return data;
  if (data?.message) return data.message;
  if (err?.message) return err.message;
  return fallback;
};

export const useDownloadValidation = (request: DownloadRequest) => {
  const [validateDownload, { isLoading: isValidating }] =
    useValidateDownloadMutation();
  const [startDownloadTask, { isLoading: isCreating }] =
    useStartDownloadTaskMutation();

  const [phase, setPhase] = useState<ValidationPhase>('idle');
  const [result, setResult] = useState<ValidateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Snap back to idle when the filter payload changes after a validation, so
  // the user can't submit against a row count that no longer reflects the form.
  const signature = JSON.stringify(request);
  const validatedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (
      validatedSignature.current !== null &&
      signature !== validatedSignature.current
    ) {
      validatedSignature.current = null;
      setPhase('idle');
      setResult(null);
      setError(null);
    }
  }, [signature]);

  const validate = async () => {
    setError(null);
    setResult(null);
    setPhase('validating');
    try {
      const res = await validateDownload(request).unwrap();
      setResult(res);
      validatedSignature.current = signature;
      setPhase('validated');
    } catch (err: any) {
      setError(errorMessage(err, 'Validation failed. Please try again.'));
      setPhase('error');
    }
  };

  const submit = async () => {
    // Guard: only submit a query the microservice confirmed is within limits.
    if (!result?.valid) return;
    setError(null);
    setPhase('creating');
    try {
      await startDownloadTask(request).unwrap();
      setPhase('done');
    } catch (err: any) {
      setError(errorMessage(err, 'Could not start the download. Please try again.'));
      setPhase('error');
    }
  };

  const reset = () => {
    validatedSignature.current = null;
    setPhase('idle');
    setResult(null);
    setError(null);
  };

  return {
    phase,
    result,
    error,
    validate,
    submit,
    reset,
    isValidating,
    isCreating,
  };
};
